import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    styled,
    IconButton,
} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment';

const CalendarContainer = styled(Box)(({ theme }) => ({
    backgroundColor: '#F2F2F2',
    borderRadius: '16px',
    overflow: 'hidden',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        padding: '12px',
        maxWidth: '100%',
    }
}));

const CalendarHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
});

const NavButton = styled(IconButton)({
    width: 36,
    height: 36,
    backgroundColor: '#F6F8FA',
    borderRadius: 8,
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.06)',
    color: '#34A853',
    padding: 0,
    '&:hover': {
        backgroundColor: '#E6E6E6',
    }
});

const MonthTitle = styled(Typography)({
    fontSize: 23,
    fontWeight: 500,
    color: '#000000',
});

const WeekRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        marginBottom: '8px',
    }
}));

const Divider = styled(Box)({
    height: 1,
    backgroundColor: '#767676',
    marginBottom: 8,
    marginTop: -4,
});

const DayHeader = styled(Typography)(({ theme }) => ({
    width: 40,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: '#767676',
    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        width: 32,
        fontSize: 12,
    }
}));

const DayContainer = styled(Box)(({ theme }) => ({
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        width: 32,
        height: 32,
    }
}));

const Day = styled(Box, {
    shouldForwardProp: (prop) =>
        !['isSelected', 'isToday', 'isDisabled', 'isInRange', 'isRangeStart', 'isRangeEnd'].includes(prop)
})(({ isSelected, isToday, isDisabled, isInRange, isRangeStart, isRangeEnd, theme }) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.2s ease',

    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        width: 28,
        height: 28,
        borderRadius: 6,
    },

    // Base styles
    ...(isDisabled && {
        backgroundColor: '#F2F2F2',
        opacity: 0.5,
        cursor: 'not-allowed',
    }),

    // Today styles
    ...(isToday && {
        border: '1px solid #34A853',
    }),

    // Selected day styles
    ...(isSelected && {
        borderWidth: 1.7,
        borderColor: '#212121',
        borderRadius: 13,
        backgroundColor: 'rgba(52, 168, 83, 0.1)',
        [theme.breakpoints.down('sm')]: {
            borderWidth: 1.5,
            borderRadius: 10,
        },
    }),

    // Range styles
    ...(isInRange && {
        backgroundColor: 'rgba(52, 168, 83, 0.25)',
    }),
    ...(isRangeStart && {
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        backgroundColor: 'rgba(52, 168, 83, 0.4)',
        [theme.breakpoints.down('sm')]: {
            borderTopLeftRadius: 6,
            borderBottomLeftRadius: 6,
        },
    }),
    ...(isRangeEnd && {
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        backgroundColor: 'rgba(52, 168, 83, 0.4)',
        [theme.breakpoints.down('sm')]: {
            borderTopRightRadius: 6,
            borderBottomRightRadius: 6,
        },
    }),

    // Hover effect for non-disabled days
    '&:hover': !isDisabled && {
        backgroundColor: isSelected ? 'rgba(52, 168, 83, 0.2)' : 'rgba(52, 168, 83, 0.1)',
    },
}));

const DayText = styled(Typography, {
    shouldForwardProp: (prop) =>
        !['isSelected', 'isDisabled'].includes(prop)
})(({ isSelected, isDisabled, theme }) => ({
    fontSize: 16,
    fontWeight: 500,
    color: isDisabled ? '#9E9E9E' : '#212121',
    ...(isSelected && {
        color: '#212121',
        fontWeight: 600,
    }),
    // Responsive styles
    [theme.breakpoints.down('sm')]: {
        fontSize: 14,
    }
}));

const SearchBarCalendar = ({
    onDayPress,
    initialDate,
    selectedStartDate,
    selectedEndDate
}) => {
    const [currentMonth, setCurrentMonth] = useState(moment());
    const today = moment().format('YYYY-MM-DD');

    // Initialize with provided dates
    useEffect(() => {
        if (initialDate) {
            setCurrentMonth(moment(initialDate));
        }
    }, [initialDate]);

    const navigateMonth = (direction) => {
        setCurrentMonth(moment(currentMonth).add(direction, 'month'));
    };

    const handleDayPress = (date) => {
        const dateString = date.format('YYYY-MM-DD');
        // Don't allow selection of past dates
        if (date.isBefore(moment(), 'day')) {
            return;
        }

        if (onDayPress) {
            onDayPress({
                dateString,
                day: date.date(),
                month: date.month() + 1,
                year: date.year()
            });
        }
    };

    const isDateInRange = (date) => {
        if (!selectedStartDate || !selectedEndDate) return false;

        const dateObj = moment(date);
        const startDateObj = moment(selectedStartDate);
        const endDateObj = moment(selectedEndDate);

        return dateObj.isBetween(startDateObj, endDateObj, 'day', '()');
    };

    const generateCalendarDays = () => {
        const days = [];

        // First day of the month
        const firstDay = moment(currentMonth).startOf('month');
        // Start from the beginning of the week containing the first day
        const startDate = moment(firstDay).startOf('week');

        // Generate a copy to work with
        const day = startDate.clone();
        const monthToRender = currentMonth.month();

        // Day headers (Mon, Tue, Wed, etc.)
        days.push(
            <WeekRow key="headers">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((header, index) => (
                    <DayHeader key={`header-${index}`}>
                        {header}
                    </DayHeader>
                ))}
            </WeekRow>
        );

        // Divider line
        days.push(<Divider key="divider" />);

        // Generate weeks
        for (let week = 0; week < 6; week++) {
            const weekDays = [];

            for (let i = 0; i < 7; i++) {
                const currentDate = day.clone();
                const dateStr = currentDate.format('YYYY-MM-DD');
                const isCurrentMonth = currentDate.month() === monthToRender;

                // Check various date statuses
                const isSelected = dateStr === selectedStartDate || dateStr === selectedEndDate;
                const isToday = dateStr === today;
                const isPast = currentDate.isBefore(moment(), 'day');
                const isDisabled = isPast || !isCurrentMonth;

                // Check range status
                const isInRange = isDateInRange(dateStr);
                const isRangeStart = dateStr === selectedStartDate && selectedEndDate;
                const isRangeEnd = dateStr === selectedEndDate && selectedStartDate;

                weekDays.push(
                    <DayContainer
                        key={`day-${dateStr}`}
                        onClick={() => !isDisabled && handleDayPress(currentDate)}
                        sx={{ opacity: isCurrentMonth ? 1 : 0.3 }}
                    >
                        <Day
                            isSelected={isSelected}
                            isToday={isToday}
                            isDisabled={isDisabled}
                            isInRange={isInRange}
                            isRangeStart={isRangeStart}
                            isRangeEnd={isRangeEnd}
                        >
                            <DayText
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                            >
                                {currentDate.date()}
                            </DayText>
                        </Day>
                    </DayContainer>
                );

                // Move to the next day
                day.add(1, 'day');
            }

            days.push(
                <WeekRow key={`week-${week}`}>
                    {weekDays}
                </WeekRow>
            );
        }

        return days;
    };

    return (
        <CalendarContainer>
            <CalendarHeader>
                <NavButton onClick={() => navigateMonth(-1)}>
                    <ChevronLeftIcon />
                </NavButton>
                <MonthTitle>{currentMonth.format('MMMM YYYY')}</MonthTitle>
                <NavButton onClick={() => navigateMonth(1)}>
                    <ChevronRightIcon />
                </NavButton>
            </CalendarHeader>
            <Box>
                {generateCalendarDays()}
            </Box>
        </CalendarContainer>
    );
};

SearchBarCalendar.propTypes = {
    onDayPress: PropTypes.func.isRequired,
    initialDate: PropTypes.string,
    selectedStartDate: PropTypes.string,
    selectedEndDate: PropTypes.string,
};

export default SearchBarCalendar;